/* jshint esversion: 6 */
import React from 'react';
import PropTypes from 'prop-types';
import FormUtilityMixin from 'app/mixins/FormUtilityMixin.js';
import Utilities from 'app/utility/Utilities.js';
import * as _ from 'underscore';
import SourceSettingsDialog from '@/shadcn/components/settings/complex/source-settings-dialog.tsx';
import {
    buildFeedSections,
    feedSourceType,
    feedSubtype,
    FEED_COMPOSITE_PATHS,
    FEED_ERROR_KEYS
} from '@/lib/feed-settings-sections.ts';
import {
    dirtySectionIds,
    sectionErrorCounts,
    sectionPaths,
    validationKeys
} from '@/lib/settings-sections.ts';
import { feedFieldError, feedUpgradeKey, feedWrites } from '@/lib/settings-changes.ts';
import { fetchInputPlaylists, inputPlaylistsRequest } from '@/lib/input-playlists.ts';

/**
 * The tab a URL names, and the URL a tab wants.
 *
 * The create path's own segments, which differ from the edit path's in one
 * place: legacy calls the first tab `setup` here and `detail` afterwards. The
 * section id stays `detail` either way so there is one mapping rather than two
 * — only the label moves, and the builder moves it.
 */
const SECTION_BY_PATH = {
    setup: 'detail',
    update: 'updates',
    hashtags: 'hashtags',
    text: 'text',
    filter: 'filters',
    location: 'location',
    advanced: 'advanced'
};

const PATH_BY_SECTION = _.invert(SECTION_BY_PATH);

class InputModal extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            input: Utilities.deepClone(this.props.input),
            fieldErrors: {},
            playlists: null,
            playlistsLoading: false,
            playlistScope: undefined
        };

        this.closeInputModal = this.closeInputModal.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onFieldChange = this.onFieldChange.bind(this);
        this.selectSection = this.selectSection.bind(this);

    }

    componentDidMount() {

        let constants = this.props.app.config.constants,
            input = this.state.input;

        // A YouTube source's playlists, for its "Post these videos" choice.
        if (!inputPlaylistsRequest(input, constants)) {
            return;
        }

        this.setState({playlistsLoading: true});

        fetchInputPlaylists(this.props.app.config.apiUrl, input, constants).then((playlists) => {
            if (!this.unmounted) {
                this.setState({playlists, playlistsLoading: false});
            }
        });

    }

    componentWillUnmount() {

        this.unmounted = true;

    }

    closeInputModal() {
        let input = this.state.input;

        if (_.isEqual(input, this.props.input)) {
            input.dirty = false;
        }

        this.props.onModalClose(input);

    }

    onSave() {

        this.props.onSave(this.state.input);

    }

    /** Which tab the current URL names. */
    currentSection() {

        let segment = this.props.router.location.pathname.split('/').pop();

        return SECTION_BY_PATH[segment] || 'detail';

    }

    selectSection(id) {

        this.props.router.navigate([
            this.props.baseUrl + this.props.linkPrefix,
            PATH_BY_SECTION[id] || 'setup'
        ].join('/'));

    }

    /**
     * Every field-level edit, through the same translator the source pages
     * use. Nothing here is create-specific: the one tab that differs differs
     * in the section spec, not in what a change means.
     */
    onFieldChange(change) {

        let resources = (this.props.user.perms && this.props.user.perms.resources) ? this.props.user.perms.resources : {},
            constants = this.props.app.config.constants,
            upgrade = feedUpgradeKey(change, resources),
            refusal = null;

        if (change.type === 'action' || change.type === 'tag-search' || change.type === 'tag-search-clear') {
            // Reconnect and the related-hashtag search both belong to a source
            // that exists; neither field is built while creating one.
            return;
        }

        if (upgrade) {
            this.props.showUpgradeModal(this.props.closeAllModals, upgrade, null, 'input_edit');
        }

        refusal = feedFieldError(change, this.state.input, constants);

        if (refusal) {
            this.setState({
                fieldErrors: Object.assign({}, this.state.fieldErrors, {[change.field.id]: refusal})
            });
            return;
        }

        if (this.state.fieldErrors[change.field.id]) {
            let cleared = Object.assign({}, this.state.fieldErrors);
            delete cleared[change.field.id];
            this.setState({fieldErrors: cleared});
        }

        feedWrites(change, this.state.input, {constants, resources, clean: this.props.input}).forEach((entry) => {
            FormUtilityMixin.requestChange.call(this, {
                currentTarget: {dataset: {field: entry.field}},
                target: {value: entry.value}
            });
        });

        // "One playlist" has no stored form until a playlist is picked, so the
        // choice itself is held here.
        if (change.field.id === 'scope') {
            this.setState({playlistScope: change.value});
        }

    }

    render() {

        let constants = this.props.app.config.constants,
            input = this.state.input,
            // The modal is only opened once an input is being configured, so a
            // missing record is a render ahead of its data rather than a state
            // the reader can reach.
            loading = _.isEmpty(input),
            /* `errors` arrives as the whole API envelope; the tree inside it is
               the same shape the source pages read. */
            validation = (this.props.errors && this.props.errors.errors)
                ? this.props.errors.errors.validation
                : null,
            sections = buildFeedSections(input, input, {
                constants,
                resources: (this.props.user.perms && this.props.user.perms.resources) ? this.props.user.perms.resources : {},
                isFreeUser: this.props.isFreeUser(),
                fastPullUser: this.props.isAgencyUser() || this.props.isPlusUser() || this.props.isAgencyPlusUser(),
                routeViewType: this.props.app.flags.routeViewType,
                fieldErrors: this.state.fieldErrors,
                validation,
                playlists: {list: this.state.playlists, loading: this.state.playlistsLoading},
                playlistScope: this.state.playlistScope,
                create: true
            }),
            paths = sectionPaths(sections, 'input', FEED_COMPOSITE_PATHS);

        return (
            <SourceSettingsDialog
                sections={sections}
                source={feedSourceType(input, constants)}
                subtype={feedSubtype(input, constants)}
                sourceName={input.name || ''}
                avatarUrl={input.icon || undefined}
                loading={loading}
                formKey={String(input.id || input.type || '')}
                onChange={this.onFieldChange}
                activeSection={this.currentSection()}
                onSectionChange={this.selectSection}
                /* The clone is diffed against the record handed in, which is
                   what the caller will get back from onSave. */
                dirty={!_.isEqual(input, this.props.input)}
                dirtySections={dirtySectionIds(paths, input, this.props.input)}
                errorCounts={sectionErrorCounts(validationKeys(this.props.app), FEED_ERROR_KEYS)}
                formError={validation ? "Check the highlighted tabs for details." : undefined}
                onSave={this.onSave}
                onCancel={this.closeInputModal}
                onClose={this.closeInputModal} />
        );
    }

};

InputModal.displayName = "InputModal";

InputModal.propTypes = {
    input: PropTypes.object,
    userPerms: PropTypes.object,
    routeViewType: PropTypes.number,
    errors: PropTypes.object,
    onModalClose: PropTypes.func,
    onSave: PropTypes.func,
    linkPrefix: PropTypes.string,
    trialEligible: PropTypes.bool,
    baseUrl: PropTypes.string,
    stateKey: PropTypes.string
};

InputModal.defaultProps = {
    input: {},
    userPerms: {},
    routeViewType: 0,
    errors: null,
    onModalClose: () => {},
    onSave: () => {},
    linkPrefix: 'new/configure/input',
    trialEligible: false,
    baseUrl: '/share/automate/',
    stateKey: 'input'
};

export default InputModal;
